<template>
  <a-card class="style1">
    <h3 class="pb-2">Connections</h3>
    <a-table :loading="isLoading" :columns="columns" :data-source="userData" size="middle" bordered rowKey="id">
      <div slot="fullname" slot-scope="text, record">
        {{ getFullName(record) }}
      </div>
      <div slot="created_at" slot-scope="text, record">
        {{ getCreatedAt(record) }}
      </div>
      <div slot="share_story_video" slot-scope="text, record">
        <template v-if="record.intro_video_slug_approved">
          <a :href="getShareStoryVideo(record)" target="_blank">{{ getShareStoryVideo(record) }}</a>
        </template>
      </div>
      <div slot="action" slot-scope="text, record">
        <a-select
          :showArrow="false"
          :default-value="record.church_status"
          @change="e => handleStatusChange(e, record)"
          style="min-width: 120px"
          :class="`select-status-${record.id} select-value-${record.church_status}`"
        >
          <a-select-option v-for="(ms, index) in getConnectionActions(record.church_status)" :value="ms.value" :key="index" :class="`select-option-${ms.value}`">
            {{ ms.text }}
          </a-select-option>
        </a-select>
      </div>
      <div slot="filterDropdown" slot-scope="{ setSelectedKeys, selectedKeys, confirm, clearFilters, column }" style="padding: 8px">
        <a-input
          v-ant-ref="c => (searchInput = c)"
          :placeholder="`Search ${column.title}`"
          :value="selectedKeys[0]"
          style="width: 190px; margin-bottom: 8px; display: block"
          @change="e => setSelectedKeys(e.target.value ? [e.target.value] : [])"
          @pressEnter="() => handleSearch(selectedKeys, confirm, column.key)"
        />
        <a-button type="primary" icon="search" size="small" style="width: 90px; margin-right: 8px" @click="() => handleSearch(selectedKeys, confirm, column.key)">Search</a-button>
        <a-button size="small" style="width: 90px" @click="() => handleReset(clearFilters)">Reset</a-button>
      </div>
      <a-icon slot="filterIcon" slot-scope="filtered" type="search" :style="{ color: filtered ? '#108ee9' : undefined }" />
    </a-table>
  </a-card>
</template>
<script>
import { slugData, empty } from '@/utils';
import { includes } from 'lodash';
import moment from 'moment';
import store from '@/store';
import UserService from '@/services/UserService';
import Constants from '@/constants.js';

const statusList = [
  { text: 'Connected', value: 'approved' },
  { text: 'Disconnected', value: 'disconnected' },
  { text: 'Under review', value: 'review' },
  { text: 'Approved', value: 'approved' },
  { text: 'Declined', value: 'declined' },
];

export default {
  data() {
    return {
      connections: [],
      searching: '',
      error: '',
      isLoading: true,
      userData: [],
      columns: [
        {
          title: 'Email',
          dataIndex: 'email',
          key: 'email',
        },
        {
          title: 'Full Name',
          dataIndex: 'fullname',
          key: 'fullname',
          scopedSlots: {
            customRender: 'fullname',
          },
        },
        {
          title: 'Created at',
          dataIndex: 'created_at',
          key: 'created_at',
          scopedSlots: {
            customRender: 'created_at',
          },
        },
        {
          title: 'Jesus Story Video',
          dataIndex: 'share_story_video',
          key: 'share_story_video',
          scopedSlots: {
            customRender: 'share_story_video',
          },
        },
        {
          title: 'Action',
          key: 'operation',
          fixed: 'right',
          width: 100,
          scopedSlots: {
            customRender: 'action',
          },
          filters: statusList.filter((s, i) => statusList.findIndex(ss => ss.value === s.value) === i),
          onFilter: (value, record) => includes(record.church_status, value),
        },
      ].map(item => {
        if (item.key !== 'operation') {
          return {
            ...item,
            scopedSlots: {
              ...item.scopedSlots,
              filterDropdown: 'filterDropdown',
              filterIcon: 'filterIcon',
            },
            onFilter: (value, record) => record[this.searchedColumn] && includes(slugData(record[this.searchedColumn]), slugData(value)),
            onFilterDropdownVisibleChange: visible => {
              if (visible) {
                setTimeout(() => {
                  this.searchInput.focus();
                }, 0);
              }
            },
          };
        }
        return item;
      }),
    };
  },
  watch: {
    connections: {
      async handler(val) {
        this.userData = val;
      },
      immediate: true,
    },
  },
  async created() {
    try {
      this.isLoading = true;
      await this.doInitData();
    } catch (error) {
      console.error({ error });
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    empty,
    getFullName(record) {
      const { firstname, lastname } = record;
      return `${firstname || ''} ${lastname || ''}`.trimLeft();
    },
    getCreatedAt(record) {
      const date = new Date(record.created_at || Date.now());
      return moment(date).format('MM/DD/YYYY');
    },
    getShareStoryVideo(record) {
      const approvedSlug = record.intro_video_slug_approved;
      return approvedSlug ? Constants.APP_URL + '/story/' + approvedSlug : '';
    },
    getConnectionActions(status) {
      return statusList.filter((s, idx) => (status === 'approved' ? [0, 1] : [2, 3, 4]).includes(idx));
    },
    async doInitData() {
      const { user } = store.state.user;
      this.connections = await UserService.getConnectRequests(user.accountId);
    },
    async handleStatusChange(value, record) {
      this.isLoading = true;

      // eslint-disable-next-line camelcase
      const { user } = store.state.user;
      const { church_status, id } = record;
      const element = document.querySelector(`.select-status-${id}`);
      // eslint-disable-next-line camelcase
      element.classList.remove(`select-value-${church_status}`);
      element.classList.add(`select-value-${value}`);

      if (value === 'disconnected') {
        await UserService.doDisconnectChurchConnect({
          id: record.id,
        });
        this.connections = this.connections.filter(r => r.id !== record.id);
      } else {
        await UserService.doUpdateUser({
          email: record.email,
          church_status: value,
        });
        const reviewConnectRequests = await UserService.getReviewConnectRequests(user.accountId);
        store.dispatch('updateUser', {
          user: {
            ...store.state.user.user,
            newConnections: reviewConnectRequests.data,
          },
        });
      }

      this.isLoading = false;
    },
    handleReset(clearFilters) {
      clearFilters();
    },
    handleSearch(selectedKeys, confirm, key) {
      confirm();
      this.searchedColumn = key;
    },
  },
};
</script>
<style lang="scss">
.select-value-review,
.select-option-review {
  color: #33cacc;
}
.select-value-approved,
.select-option-approved {
  color: #78beff;
}
.select-value-declined,
.select-option-declined,
.select-value-disconnected,
.select-option-disconnected {
  color: #eb685f;
}
</style>

<style lang="scss" scoped>
.style1 {
  margin: 5px;
}
</style>
